import { GET_COURSE_PURCHASES } from '../../../apollo/courses.ts';
import Button from '../../common/button.tsx';
import { getDateString } from '../../../utils/common-utils.ts';
import { Dispatch, SetStateAction, useState } from 'react';
import Modal from '../../common/modal.tsx';
import { useQuery } from '@apollo/client';
import UserAvatar from '../../users/user-avatar.tsx';
import QuizScoresModal from './quiz-scores-modal.tsx';
import TestScoresModal from './test-scores-modal.tsx';
import { GetCourseQuery } from '../../../apollo/__generated__/graphql.ts';
import { uniqBy } from 'lodash';
import { useFetchMore, useGetMe } from '../../../utils/hooks.ts';

interface IProps {
  course?: GetCourseQuery['course']['getCourse'];
}

interface IUserInfo {
  id: number;
  name: string;
}

const PurchasesTab = ({ course }: IProps) => {
  const FETCH_LIMIT = 20;
  const { data, fetchMore, loading } = useQuery(GET_COURSE_PURCHASES, {
    variables: {
      filter: {
        courseId: course?.id,
      },
      paging: {
        page: 1,
        limit: FETCH_LIMIT,
      },
    },
  });
  const [page, setPage] = useState(1);
  const { isStaff, isAdmin, isTeacher, isTA } = useGetMe();

  const [userForReviewQuiz, setUserIdForReviewQuiz] =
    useState<IUserInfo | null>(null);
  const [userForReviewTest, setUserIdForReviewTest] =
    useState<IUserInfo | null>(null);

  const members = data?.userPurchase.getUserPurchases.results;

  const onFetchMore = (setFetchMore: Dispatch<SetStateAction<boolean>>) => {
    const total = Number(data?.userPurchase.getUserPurchases.total);
    if (total && Number(members?.length) < total) {
      const nextPage = page + 1;
      setPage(nextPage);
      setFetchMore(true);
      return fetchMore({
        variables: {
          paging: {
            page: nextPage,
            limit: FETCH_LIMIT,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.userPurchase.getUserPurchases.results)
            return prev;

          const union = [
            ...(prev.userPurchase?.getUserPurchases?.results || []),
            ...fetchMoreResult.userPurchase.getUserPurchases.results,
          ];

          const unique = uniqBy(union, 'id');

          if (unique.length !== union.length)
            console.error('duplicates on merge detected'); // TODO remove after dev stage

          return {
            userPurchase: {
              ...prev.userPurchase,
              getUserPurchases: {
                ...fetchMoreResult.userPurchase.getUserPurchases,
                results: unique,
              },
            },
          };
        },
      });
    }
  };

  const { ref, isFetchMore } = useFetchMore(onFetchMore);

  return (
    <div className={'mt-6'}>
      <div className={'mb-10 mt-2'}>
        {members?.map((member, index, array) => {
          return (
            !!member.user && (
              <div
                key={member.id}
                ref={index === array.length - 5 ? ref : null}
                className={
                  'mt-1 flex items-center justify-between rounded-lg border border-table-border p-2 text-sm'
                }
              >
                <div className={'flex'}>
                  <UserAvatar name={member.user.name || member.user.email} />
                  <div className={'ml-2'}>
                    <p className={'font-semibold text-blue'}>
                      {member.user.name || member.user.email}
                    </p>
                    <p>{getDateString(member.createdAt)}</p>
                  </div>
                </div>

                <div className={'flex'}>
                  {(isAdmin || isTeacher || isStaff || isTA) && (
                    <>
                      <Button
                        white
                        onClick={() => {
                          if (member.user) {
                            setUserIdForReviewQuiz({
                              name: member.user.name || member.user.email,
                              id: member.user.id,
                            });
                          }
                        }}
                      >
                        Quiz scores
                      </Button>
                      <Button
                        white
                        className={'mx-2'}
                        onClick={() => {
                          if (member.user) {
                            setUserIdForReviewTest({
                              name: member.user.name || member.user.email,
                              id: member.user.id,
                            });
                          }
                        }}
                      >
                        Test scores
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )
          );
        })}
        {!members?.length && (
          <div className={'mt-4 text-center text-sm'}>No students yet</div>
        )}
        {(loading || isFetchMore) && (
          <div className={'mt-1 h-3 animate-pulse rounded-lg bg-blue'}></div>
        )}
      </div>

      <Modal
        title={`Quiz Scores for ${userForReviewQuiz?.name}`}
        onClose={() => setUserIdForReviewQuiz(null)}
        isOpen={!!userForReviewQuiz}
      >
        <QuizScoresModal userId={userForReviewQuiz?.id} courseId={course?.id} />
      </Modal>

      <Modal
        title={`Test Scores for ${userForReviewTest?.name}`}
        onClose={() => setUserIdForReviewTest(null)}
        isOpen={!!userForReviewTest}
      >
        <TestScoresModal userId={userForReviewTest?.id} courseId={course?.id} />
      </Modal>
    </div>
  );
};
export default PurchasesTab;
